import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { HiArrowRight, HiSparkles } from 'react-icons/hi';
import { FaHome, FaBriefcase, FaEnvelope, FaSearchLocation } from 'react-icons/fa';
import gsap from 'gsap';
import useHeroEntrance from '../hooks/useHeroEntrance';
import { prefersReducedMotion } from '../hooks/useReducedMotion';

const quickLinks = [
  { icon: <FaHome />,            title: 'Home',         description: 'Back to the main page', to: '/',          color: 'from-nexcess-blue to-nexcess-cyan' },
  { icon: <FaSearchLocation />,  title: 'Services',     description: 'See what we offer',     to: '/services',  color: 'from-nexcess-cyan to-nexcess-purple' },
  { icon: <FaBriefcase />,       title: 'Projects',     description: 'Browse our work',       to: '/projects',  color: 'from-nexcess-purple to-nexcess-blue' },
  { icon: <FaEnvelope />,        title: 'Contact',      description: 'Get in touch with us',  to: '/contact',   color: 'from-nexcess-blue to-nexcess-purple' },
];

const NotFound = () => {
  const heroBadgeRef = useRef(null);
  const heroH1Ref    = useRef(null);
  const heroParaRef  = useRef(null);
  const heroCtaRef   = useRef(null);
  useHeroEntrance([
    heroBadgeRef,
    { ref: heroH1Ref, heading: true },
    heroParaRef,
    heroCtaRef,
  ]);

  /* Quick-link cards: stagger entrance + smooth hover lift */
  const linksWrapRef = useRef(null);
  useEffect(() => {
    if (!linksWrapRef.current) return;
    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray('.nf-link-card');
      if (!cards.length) return;
      if (prefersReducedMotion()) {
        gsap.set(cards, { opacity: 1, y: 0, scale: 1 });
        return;
      }
      gsap.set(cards, { opacity: 0, y: 32, scale: 0.96, willChange: 'transform' });
      gsap.to(cards, {
        opacity: 1, y: 0, scale: 1,
        duration: 0.85, stagger: 0.1, ease: 'power3.out',
        delay: 0.6,
      });
      cards.forEach((c) => {
        const onEnter = () => gsap.to(c, { y: -8, scale: 1.02, duration: 0.4, ease: 'power3.out', overwrite: 'auto' });
        const onLeave = () => gsap.to(c, { y: 0, scale: 1, duration: 0.6, ease: 'power3.out', overwrite: 'auto' });
        c.addEventListener('mouseenter', onEnter);
        c.addEventListener('mouseleave', onLeave);
      });
    }, linksWrapRef);
    return () => ctx.revert();
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="relative pt-36 pb-20 overflow-hidden bg-paper">
        <div className="absolute inset-0 bg-mesh-soft pointer-events-none" />
        <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-nexcess-blue/8 blur-3xl animate-blob pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-nexcess-purple/8 blur-3xl animate-blob pointer-events-none" style={{ animationDelay: '4s' }} />

        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div ref={heroBadgeRef} className="badge mb-5 mx-auto">
              <HiSparkles /> Error · 404
            </div>
            <h1 ref={heroH1Ref} className="display-hero mb-6">
              Page <span className="gradient-text">Not Found</span>
            </h1>
            <p ref={heroParaRef} className="narrative-lg mb-10">
              The page you are looking for might have been removed, had its name changed,
              or is temporarily unavailable. Don't worry — the way back is just a click away.
            </p>
            <div ref={heroCtaRef} className="flex flex-wrap gap-4 justify-center">
              <Link to="/" className="btn btn-primary inline-flex" style={{ willChange: 'transform' }}>
                <FaHome /> Return Home <HiArrowRight />
              </Link>
              <Link to="/contact" className="btn btn-outline inline-flex" style={{ willChange: 'transform' }}>
                Contact Support
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK LINKS */}
      <section className="section-padding bg-paper-2">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <div className="badge mb-4 mx-auto">Where to next</div>
            <h2 className="heading-xl mb-4">
              Looking for <span className="gradient-text">something specific?</span>
            </h2>
            <p className="narrative-lg">
              Try one of these popular destinations — we'll get you back on track in seconds.
            </p>
          </div>

          <div ref={linksWrapRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickLinks.map((link) => (
              <Link
                key={link.title}
                to={link.to}
                className="nf-link-card group bg-white rounded-2xl p-7 border border-hairline shadow-soft block"
                style={{ willChange: 'transform' }}
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${link.color} flex items-center justify-center text-white text-2xl mb-5 shadow-soft group-hover:scale-110 transition-transform duration-300`}>
                  {link.icon}
                </div>
                <h3 className="heading-sm mb-2">{link.title}</h3>
                <p className="text-sm text-ink-soft leading-relaxed mb-4">{link.description}</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-nexcess-blue group-hover:gap-2.5 transition-all">
                  Visit <HiArrowRight />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default NotFound;
